import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {usePullRequestBasePath} from '../../stores/layout-selectors'
import type {ConversationComment} from '../../page-data/payloads/conversation-comments'
import {useRef, useState} from 'react'
import type {
  UpdateIssueCommentResponse,
  UpdateIssueCommentVariables,
} from '../../mutations/use-update-issue-comment-mutation'
import {validateCommentEdit, type ValidationResult} from '@github-ui/entity-validators'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {md2html} from '@github-ui/md2html'
import type {UseMutationResult} from '@github-ui/react-query'
import {StaleContentError} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

export type ConversationItemBodyEditManager = ReturnType<typeof useBodyEditManager>

type UpdateItemMutation = UseMutationResult<UpdateIssueCommentResponse, Error, UpdateIssueCommentVariables, unknown>

/**
 * Manages draft and edit mutation state for issue comments.
 * @param mutation Mutation to use for normal edits (through the comment editor)
 * @param directEditMutation Mutation to use for 'direct' edits (ie, by interacting with task lists)
 * Direct edits use separate mutation to opt out of optimistic updates (which don't work with checkboxes because
 * md2Html doesn't support tasklists) and they don't need the success message since the feedback is the HTML state
 */
export function useBodyEditManager(
  comment: ConversationComment,
  mutation: UpdateItemMutation,
  directEditMutation: UpdateItemMutation,
) {
  const basePath = usePullRequestBasePath()

  const [draftBody, setDraftBody, clearDraftBody] = useLocalStorage<string | null>(
    `${basePath}-conversation-${comment.type}-${comment.id}`,
    null,
  )

  const [isEditing, setIsEditing] = useState(false)

  /** Keep the version hash from the time the edit started, so a subsequent live update is ignored while editing. */
  const bodyVersionRef = useRef(comment.bodyVersion)

  const startEditing = () => {
    setIsEditing(true)
    bodyVersionRef.current = comment.bodyVersion
  }

  // Use the current body as initial value without having to write it as an unecessary draft
  const editingBody = isEditing ? (draftBody ?? comment.body) : null

  const cancel = () => {
    mutation.reset()
    setIsEditing(false)
    if (!(mutation.error instanceof StaleContentError)) clearDraftBody()
  }

  const submit = () => {
    if (!editingBody) return

    mutation.mutate(
      {id: comment.id, bodyVersion: bodyVersionRef.current, newBody: editingBody},
      {
        // don't clear the persisted value until the request completes successfully, so users can restore on error
        onSuccess: () => clearDraftBody(),
        // revert back to edit mode on error
        onError: () => startEditing(),
      },
    )

    // leave edit mode immediately to show optimistic update
    setIsEditing(false)
  }

  const pendingBody = mutation.isPending ? mutation.variables.newBody : null

  const validationResult: ValidationResult | undefined =
    editingBody !== null ? validateCommentEdit(comment.body, editingBody) : undefined

  const handleDirectMarkdownChange = async (value: string) => {
    await directEditMutation.mutateAsync({id: comment.id, newBody: value, bodyVersion: comment.bodyVersion})
  }

  const dismissError = () => {
    mutation.reset()
    directEditMutation.reset()
  }

  return {
    /** Start editing. Undefined if not allowed or disabled. */
    startEditIfAllowed:
      comment.viewerCanUpdate && isFeatureEnabled('prs_new_conversation_comments_api') ? startEditing : undefined,
    cancel,
    submit,
    handleDraftChange: setDraftBody,
    handleDirectMarkdownChange,
    dismissError,
    isPending: pendingBody !== null || directEditMutation.isPending,
    isEditing: editingBody !== null,
    /** Markdown comment body currently being edited. `null` if not editing the comment. */
    editingBody,
    /** Save request error, if the request failed. Excludes stale error (handled separately). */
    error: mutation.error || directEditMutation.error,
    validationResult,
    /** HTML string to render for comment body. Will be the updated body if a request is pending. */
    optimisticBodyHtml: pendingBody ? md2html(pendingBody) : comment.bodyHTML,
    /** Optimistic body Markdown value. */
    optimisticBody: pendingBody ?? comment.body,
  }
}
